import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {Plus, Edit, Trash2, Search, Filter, Star, Eye, Calendar, Tag as TagIcon, FileText, RefreshCw, ExternalLink, Copy} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Article {
  _id: string
  title: string
  titleEn?: string
  slug: string
  excerpt?: string
  content: string
  category: 'news' | 'guide' | 'beanstory' | 'events'
  tags?: string[]
  featuredImage?: string
  images?: string[]
  relatedProducts?: string[]
  relatedStores?: string[]
  author?: string
  publishedAt?: string
  status: 'draft' | 'published' | 'archived'
  isFeatured: boolean
  viewCount?: number
  seoTitle?: string
  seoDescription?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

const ArticleManager: React.FC = () => {
  const navigate = useNavigate()
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')


  const categories = [
    { value: 'news', label: '最新消息', color: 'bg-blue-600' },
    { value: 'guide', label: '品鑑指南', color: 'bg-green-600' },
    { value: 'beanstory', label: '豆誌故事', color: 'bg-purple-600' },
    { value: 'events', label: '活動紀錄', color: 'bg-orange-600' }
  ]

  useEffect(() => {
    fetchArticles()
  }, [])

  const fetchArticles = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.articles.list({
        sort: { createdAt: -1 }
      })
      setArticles(response.list || [])
    } catch (error) {
      console.error('Failed to fetch articles:', error)
      toast.error('載入文章失敗')
    } finally {
      setLoading(false)
    }
  }





  const handleDelete = async (articleId: string) => {
    if (!confirm('確定要刪除這篇文章嗎？此操作無法復原。')) return

    try {
      await lumi.entities.articles.delete(articleId)
      toast.success('文章刪除成功')
      fetchArticles()
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('文章刪除失敗')
    }
  }

  const toggleFeatured = async (article: Article) => {
    try {
      await lumi.entities.articles.update(article._id, {
        isFeatured: !article.isFeatured,
        updatedAt: new Date().toISOString()
      })
      toast.success(`已${!article.isFeatured ? '設為' : '取消'}精選文章`)
      fetchArticles()
    } catch (error) {
      console.error('Toggle featured failed:', error)
      toast.error('操作失敗')
    }
  }

  const toggleStatus = async (article: Article) => {
    const statusOrder: Article['status'][] = ['draft', 'published', 'archived']
    const currentIndex = statusOrder.indexOf(article.status)
    const newStatus = statusOrder[(currentIndex + 1) % statusOrder.length]
    
    try {
      await lumi.entities.articles.update(article._id, {
        status: newStatus,
        publishedAt: newStatus === 'published' && !article.publishedAt 
          ? new Date().toISOString() 
          : article.publishedAt,
        updatedAt: new Date().toISOString()
      })
      toast.success(`文章狀態已更新為：${
        newStatus === 'draft' ? '草稿' : 
        newStatus === 'published' ? '已發布' : '已封存'
      }`)
      fetchArticles()
    } catch (error) {
      console.error('Toggle status failed:', error)
      toast.error('操作失敗')
    }
  }

  // 生成文章URL
  const getArticleUrl = (article: Article) => {
    return `https://www.ongucoffee.com/zh/lab/${article.category}/${article.slug}`
  }

  // 複製URL到剪貼簿
  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
    toast.success('URL 已複製到剪貼簿')
  }

  // 過濾文章
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (article.titleEn?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = filterCategory === 'all' || article.category === filterCategory
    const matchesStatus = filterStatus === 'all' || article.status === filterStatus
    
    return matchesSearch && matchesCategory && matchesStatus
  })

  // 統計資訊
  const stats = {
    total: articles.length,
    published: articles.filter(a => a.status === 'published').length,
    draft: articles.filter(a => a.status === 'draft').length,
    featured: articles.filter(a => a.isFeatured).length
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-light tracking-wider mb-2">文章管理</h1>
            <p className="text-gray-400">醞玖製研中心內容管理</p>
          </div>
          <button
            onClick={() => navigate('/admin/lab/new')}
            className="flex items-center px-6 py-3 bg-gray-400 text-black hover:bg-gray-300 transition-colors duration-200 text-sm font-light tracking-wider"
          >
            <Plus size={16} className="mr-2" />
            新增文章
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-950 border border-gray-800 p-4">
            <div className="text-2xl font-light text-gray-100 mb-1">{stats.total}</div>
            <div className="text-xs text-gray-400 tracking-wider">總文章數</div>
          </div>
          <div className="bg-gray-950 border border-gray-800 p-4">
            <div className="text-2xl font-light text-green-400 mb-1">{stats.published}</div>
            <div className="text-xs text-gray-400 tracking-wider">已發布</div>
          </div>
          <div className="bg-gray-950 border border-gray-800 p-4">
            <div className="text-2xl font-light text-yellow-400 mb-1">{stats.draft}</div>
            <div className="text-xs text-gray-400 tracking-wider">草稿</div>
          </div>
          <div className="bg-gray-950 border border-gray-800 p-4">
            <div className="text-2xl font-light text-purple-400 mb-1">{stats.featured}</div>
            <div className="text-xs text-gray-400 tracking-wider">精選文章</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-gray-950 border border-gray-800">
          <div className="flex items-center space-x-2 flex-1 min-w-64">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="搜尋文章標題或摘要..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-gray-500 focus:outline-none"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
            >
              <option value="all">所有分類</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
          >
            <option value="all">所有狀態</option>
            <option value="published">已發布</option>
            <option value="draft">草稿</option>
            <option value="archived">已封存</option>
          </select>
        </div>

        {/* Articles List */}
        <div className="space-y-4">
          {filteredArticles.map((article) => {
            const category = categories.find(cat => cat.value === article.category)
            
            return (
              <motion.div
                key={article._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-950 border border-gray-800 hover:border-gray-600 transition-all duration-200"
              >
                <div className="flex flex-col md:flex-row gap-4 p-4">
                  {/* Featured Image */}
                  <div className="w-full md:w-48 h-32 flex-shrink-0 overflow-hidden bg-gray-900">
                    {article.featuredImage ? (
                      <img
                        src={article.featuredImage}
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText size={32} className="text-gray-700" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {/* Category Badge */}
                          <span className={`${category?.color} px-2 py-1 text-xs text-white font-light tracking-wider`}>
                            {category?.label}
                          </span>
                          
                          {/* Status Badge */}
                          <span className={`px-2 py-1 text-xs font-light tracking-wider ${
                            article.status === 'published' ? 'bg-green-600 text-white' :
                            article.status === 'draft' ? 'bg-yellow-600 text-black' :
                            'bg-gray-600 text-white'
                          }`}>
                            {article.status === 'published' ? '已發布' : 
                             article.status === 'draft' ? '草稿' : '已封存'}
                          </span>

                          {/* Featured Star */}
                          {article.isFeatured && (
                            <Star size={14} className="text-yellow-400 fill-current" />
                          )}
                        </div>

                        <h3 className="text-lg font-light text-gray-100 tracking-wide mb-1 line-clamp-1">
                          {article.title}
                        </h3>
                        
                        {article.titleEn && (
                          <p className="text-sm text-gray-500 mb-2 line-clamp-1">{article.titleEn}</p>
                        )}

                        {article.excerpt && (
                          <p className="text-sm text-gray-400 line-clamp-2 mb-3">{article.excerpt}</p>
                        )}

                        {/* Tags */}
                        {article.tags && article.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {article.tags.slice(0, 5).map((tag, index) => (
                              <span key={index} className="text-xs px-2 py-1 bg-gray-800 text-gray-400 border border-gray-700">
                                #{tag}
                              </span>
                            ))}
                            {article.tags.length > 5 && (
                              <span className="text-xs text-gray-500">+{article.tags.length - 5}</span>
                            )}
                          </div>
                        )}

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                          {article.author && (
                            <span>作者：{article.author}</span>
                          )}
                          {article.publishedAt && (
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {new Date(article.publishedAt).toLocaleDateString('zh-TW')}
                            </span>
                          )}
                          {article.viewCount !== undefined && (
                            <span className="flex items-center gap-1">
                              <Eye size={12} />
                              {article.viewCount} 次瀏覽
                            </span>
                          )}
                        </div>

                        {/* URL Display */}
                        <div className="mt-3 flex items-center gap-2 bg-gray-900 border border-gray-700 px-3 py-2 rounded">
                          <a 
                            href={getArticleUrl(article)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-xs text-gray-400 hover:text-gray-200 truncate transition-colors"
                            title={getArticleUrl(article)}
                          >
                            {getArticleUrl(article)}
                          </a>
                          <button
                            onClick={() => copyUrl(getArticleUrl(article))}
                            className="p-1 text-gray-400 hover:text-gray-200 transition-colors flex-shrink-0"
                            title="複製 URL"
                          >
                            <Copy size={12} />
                          </button>
                          <a
                            href={getArticleUrl(article)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-gray-400 hover:text-gray-200 transition-colors flex-shrink-0"
                            title="在新分頁開啟"
                          >
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex md:flex-col gap-2 md:justify-start">
                    <a
                      href={getArticleUrl(article)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-green-400 transition-colors text-xs border border-gray-700 hover:border-green-400 flex items-center justify-center"
                      title="預覽文章（無論狀態）"
                    >
                      <ExternalLink size={14} />
                    </a>
                    <button
                      onClick={() => toggleStatus(article)}
                      className="p-2 text-gray-400 hover:text-gray-100 transition-colors text-xs border border-gray-700 hover:border-gray-500"
                      title="切換狀態"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => toggleFeatured(article)}
                      className={`p-2 transition-colors text-xs border ${
                        article.isFeatured 
                          ? 'text-yellow-400 border-yellow-400/50 hover:border-yellow-400' 
                          : 'text-gray-400 border-gray-700 hover:text-yellow-400 hover:border-yellow-400/50'
                      }`}
                      title={article.isFeatured ? '取消精選' : '設為精選'}
                    >
                      <Star size={14} className={article.isFeatured ? 'fill-current' : ''} />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/lab/edit/${article._id}`)}
                      className="p-2 text-gray-400 hover:text-blue-400 transition-colors text-xs border border-gray-700 hover:border-blue-400"
                      title="編輯文章"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(article._id)}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors text-xs border border-gray-700 hover:border-red-400"
                      title="刪除文章"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-12 bg-gray-950 border border-gray-800">
            <FileText size={48} className="mx-auto text-gray-700 mb-4" />
            <p className="text-gray-400">沒有找到符合條件的文章</p>
            <button
              onClick={() => navigate('/admin/lab/new')}
              className="mt-4 text-sm text-gray-400 hover:text-gray-200 underline"
            >
              立即創建第一篇文章
            </button>
          </div>
        )}
      </div>


    </div>
  )
}

export default ArticleManager
